// The import runs in chunks, one call (and one transaction) each, so a big file is not
// stopped by the 8 seconds of a database call or by the size of a request. The limit of
// the plan is checked for the whole file before the first chunk (fn_usage_limit_check)
// and again by the database in every chunk, so chunks that run are the rows the plan
// still had room for. A refusal or a failure in the middle leaves the earlier chunks
// imported and says how many rows were not (docs/import/usage-limits.md §9).

import type {
  ImportRefusalType,
  ImportBlockType,
  ImportUsageStatusType,
  ManageImportCandidatesResultType,
  ManageImportContractsResultType,
} from "@/utils/serverActions/import/importActions";

/**
 * Rows of a chunk. Candidates took 2 to 5 seconds per 1,000 rows in the local
 * measurement (8 seconds is the limit of a call) and contracts, with their client,
 * patients and weekly schedule, about 10 ms per row: 200 rows is 2 seconds.
 */
export const IMPORT_CHUNK_ROWS = { candidates: 1000, contracts: 200 } as const;

/**
 * Rows of a file the preview of candidates can check at once: the preview sends every
 * row in one request (about 250 bytes each) and the request body is limited to 4 MB
 * (next.config.mjs; Vercel allows 4.5 MB).
 */
export const MAX_PREVIEW_ROWS = 12000;

export const chunkRows = <T>(rows: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let start = 0; start < rows.length; start += size) {
    chunks.push(rows.slice(start, start + size));
  }
  return chunks;
};

/** Why an import stopped before running every chunk. */
export type ImportStopType = {
  refused?: ImportRefusalType;
  usage?: ImportUsageStatusType;
  block?: ImportBlockType;
  /** The message of an error that interrupted it. */
  interrupted?: string;
  /** Rows of the chunks that did not run. */
  rowsNotRun: number;
};

/** Adds up the results of the chunks of a candidates import (rows keep their number in the file). */
export const mergeCandidatesResults = (
  results: ManageImportCandidatesResultType[],
  offsets: number[],
  stop?: ImportStopType,
): ManageImportCandidatesResultType => ({
  total_processed: results.reduce((sum, result) => sum + result.total_processed, 0),
  successful_imports: results.reduce((sum, result) => sum + result.successful_imports, 0),
  updated_found: results.reduce((sum, result) => sum + result.updated_found, 0),
  skipped_found: results.reduce((sum, result) => sum + result.skipped_found, 0),
  errors_found: results.reduce((sum, result) => sum + result.errors_found, 0),
  not_added_candidates: results.flatMap((result, index) =>
    result.not_added_candidates.map((candidate) => ({
      ...candidate,
      row_index: candidate.row_index + offsets[index],
    })),
  ),
  ...(stop ? { ...stop } : {}),
});

/** Adds up the results of the chunks of a contracts import (rows keep their number in the file). */
export const mergeContractsResults = (
  results: ManageImportContractsResultType[],
  offsets: number[],
  stop?: ImportStopType,
): ManageImportContractsResultType => {
  const shift = <T extends { row_number: number }>(details: T[], offset: number): T[] =>
    details.map((detail) => ({ ...detail, row_number: detail.row_number + offset }));

  return {
    total_processed: results.reduce((sum, result) => sum + result.total_processed, 0),
    successful_imports: results.reduce((sum, result) => sum + result.successful_imports, 0),
    duplicates_found: results.reduce((sum, result) => sum + result.duplicates_found, 0),
    errors_found: results.reduce((sum, result) => sum + result.errors_found, 0),
    successful_details: results.flatMap((result, index) => shift(result.successful_details, offsets[index])),
    duplicate_details: results.flatMap((result, index) => shift(result.duplicate_details, offsets[index])),
    error_details: results.flatMap((result, index) => shift(result.error_details, offsets[index])),
    all_results: results.flatMap((result, index) => shift(result.all_results, offsets[index])),
    ...(stop ? { ...stop } : {}),
  };
};
